import { useEffect, useId, useRef } from 'react';

import { BackupLevel } from '../api';
import { BackupChoice, BackupChooser } from '../hooks';

import '../../DashboardV2/dashboardV2.css';
import '../../PaySurge/paySurge.css';

/*
 * GRAVY-10898 — the on/off control for "Auto backups" at one level (client,
 * order or job), and the warning every change goes through.
 *
 * No prototype exists for it. It is built from what the app already has: the
 * surge pay switch (`.paysurge-switch`), Dashboard V2's quiet link and the
 * Invite backups modal's yellow inline confirm. Each piece is wrapped in
 * `.dashv2` so the Dashboard V2 colour tokens resolve on the Order, Job and
 * client pages too.
 *
 * Turning auto backups on is not a display setting: once on, GravyWork itself
 * INVITES backups at the 3-hour confirmation check (2 per hired worker who
 * hasn't confirmed, from the client's preferred pool), a worker who accepts a
 * full shift goes on standby, and the earliest standby is HIRED AUTOMATICALLY
 * when a spot opens. Turning it off stops all of that. So every
 * change that moves the value asks first, says what it does and at what scope,
 * and focuses Cancel so Enter on the warning never commits by accident.
 */

const TITLE_SCOPE: Record<BackupLevel, string> = {
  client: 'this client',
  order: 'this order',
  job: 'this job',
};

const ON_SCOPE: Record<BackupLevel, string> = {
  client: 'every job of this client',
  order: 'every job in this order',
  job: 'this job',
};

const OFF_SCOPE: Record<BackupLevel, string> = {
  client: 'any job of this client',
  order: 'any job in this order',
  job: 'this job',
};

// A lower level's own setting wins over this one (job ?? order ?? client), so
// a client or order change does not reach jobs or orders set on their own.
const OVERRIDE_NOTE: Partial<Record<BackupLevel, string>> = {
  client: 'Orders and jobs set on their own keep their setting.',
  order: 'Jobs set on their own keep their setting.',
};

const PARENT: Partial<Record<BackupLevel, 'order' | 'client'>> = {
  job: 'order',
  order: 'client',
};

export const RESET_LABEL: Partial<Record<BackupLevel, string>> = {
  job: 'Use order setting',
  order: 'Use client setting',
};

const SOURCE_TEXT: Record<BackupLevel, Partial<Record<BackupLevel, string>>> = {
  client: {},
  order: {
    order: 'Set for this order',
    client: 'Using the client’s setting',
  },
  job: {
    job: 'Set for this job',
    order: 'Using the order’s setting',
    client: 'Using the client’s setting',
  },
};

export type BackupCounts = { onStandby: number; invited: number };

export const backupCountsText = (counts?: BackupCounts | null) => {
  if (!counts) return '';
  const parts: string[] = [];
  if (counts.onStandby > 0) parts.push(`${counts.onStandby} on standby`);
  if (counts.invited > 0) parts.push(`${counts.invited} invited`);
  return parts.join(' · ');
};

type ConfirmProps = {
  level: BackupLevel;
  choice: BackupChoice;
  onCancel: () => void;
  onConfirm: () => void;
  testId: string;
  /** The global switch, when known. Off: the change waits for it. */
  switchOn?: boolean;
};

export const BackupsConfirm = ({
  level,
  choice,
  onCancel,
  onConfirm,
  testId,
  switchOn,
}: ConfirmProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Escape closes THIS warning only. On window, in the capture phase, so it
  // runs before the document-level Escape handlers of whatever this sits in
  // (the Invite backups modal, the Shift Detail panel, the client modal) and
  // stops the event before one of them closes too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const parent = PARENT[level];
  const followsParent = choice.enabled === null && parent;
  const on = choice.turningOn;
  const title = followsParent
    ? `Use the ${parent}’s setting? Auto backups turn ${on ? 'on' : 'off'} for ${TITLE_SCOPE[level]}.`
    : `Turn ${on ? 'on' : 'off'} auto backups for ${TITLE_SCOPE[level]}?`;
  const goLabel = followsParent
    ? RESET_LABEL[level]
    : `Turn ${on ? 'on' : 'off'} for ${TITLE_SCOPE[level]}`;
  const note = OVERRIDE_NOTE[level];

  return (
    <div
      className="dashv2-scope dashv2-review-confirm dashv2-backup-confirm dashv2-bkpconfirm"
      role="alertdialog"
      aria-label={title}
      data-testid={testId}
      // Inside a clickable table row on the Order page: a click here must not
      // also select the job.
      onClick={(e) => e.stopPropagation()}
    >
      <p className="dashv2-backup-confirm-title">{title}</p>
      {on ? (
        <ul>
          <li>
            GravyWork will invite backups automatically at the 3-hour check when
            hired workers haven’t confirmed (2 per silent worker, from the
            client’s preferred pool). Texts at night wait until 8 AM.
          </li>
          <li>Ops can still invite backups by hand.</li>
          <li>
            Workers who accept a shift that is already full go on standby.
          </li>
          <li>
            When a spot opens, the earliest standby is hired automatically,
            without asking ops.
          </li>
          <li>
            This applies to {ON_SCOPE[level]}.{note ? ` ${note}` : ''}
          </li>
          {switchOn === false && (
            <li>
              The auto backups master switch is off, so this starts when it is
              turned on.
            </li>
          )}
        </ul>
      ) : (
        <p>
          No automatic backup invites, and nobody will be hired from standby on{' '}
          {OFF_SCOPE[level]}. Backups already on standby stay listed and their
          standby ends when the shift starts.{note ? ` ${note}` : ''}
        </p>
      )}
      <div className="dashv2-review-confirm-actions">
        <button
          ref={cancelRef}
          type="button"
          className="dashv2-review-confirm-cancel"
          data-testid={`${testId}-cancel`}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="dashv2-review-confirm-go"
          data-testid={`${testId}-go`}
          onClick={onConfirm}
        >
          {goLabel}
        </button>
      </div>
    </div>
  );
};

export type BackupsControlProps = {
  level: BackupLevel;
  /** The visible name, e.g. "Auto backups for this job". */
  label: string;
  value: boolean;
  source: BackupLevel;
  /** What clearing this level's override gives; null where there is none. */
  inherited: boolean | null;
  counts?: BackupCounts | null;
  switchOn?: boolean;
  saving: boolean;
  error: string | null;
  chooser: BackupChooser;
  /** Prefix for every test id inside the control. */
  testId: string;
  compact?: boolean;
};

/** The switch, where its value comes from, the counts and the reset. */
export const BackupsControl = ({
  level,
  label,
  value,
  source,
  inherited,
  counts,
  switchOn,
  saving,
  error,
  chooser,
  testId,
  compact = false,
}: BackupsControlProps) => {
  const labelId = useId();
  const busy = saving || chooser.choice != null;
  const sourceText = SOURCE_TEXT[level][source];
  const countsText = backupCountsText(counts);
  const overridden = source === level && inherited != null;

  return (
    <div
      className={`dashv2 dashv2-bkpset${compact ? ' is-compact' : ''}`}
      data-testid={testId}
      data-value={value ? 'on' : 'off'}
      data-source={source}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dashv2-bkpset-row">
        <span className="dashv2-bkpset-label" id={labelId}>
          {label}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-labelledby={labelId}
          className="paysurge-switch"
          data-testid={`${testId}-toggle`}
          disabled={busy}
          onClick={() => chooser.request(!value, !value, value)}
        />
        <span className="dashv2-bkpset-state" data-testid={`${testId}-state`}>
          {saving ? 'Saving…' : value ? 'On' : 'Off'}
        </span>
      </div>
      {(sourceText || countsText || overridden || switchOn === false) && (
        <div className="dashv2-bkpset-meta">
          {sourceText && (
            <span data-testid={`${testId}-source`}>{sourceText}</span>
          )}
          {countsText && (
            <span data-testid={`${testId}-counts`}>{countsText}</span>
          )}
          {overridden && (
            <button
              type="button"
              className="dashv2-backup-quiet"
              data-testid={`${testId}-reset`}
              disabled={busy}
              onClick={() => chooser.request(null, inherited, value)}
            >
              {RESET_LABEL[level]}
            </button>
          )}
          {switchOn === false && (
            <span data-testid={`${testId}-switch-off`}>
              The auto backups master switch is off
            </span>
          )}
        </div>
      )}
      {error && (
        <p
          className="dashv2-bkpset-error"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

/** The control with its warning directly under it. */
const BackupsSetting = (props: BackupsControlProps) => (
  <>
    <BackupsControl {...props} />
    {props.chooser.choice && (
      <BackupsConfirm
        level={props.level}
        choice={props.chooser.choice}
        onCancel={props.chooser.cancel}
        onConfirm={props.chooser.confirm}
        testId={`${props.testId}-confirm`}
        switchOn={props.switchOn}
      />
    )}
  </>
);

export default BackupsSetting;
